import { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { RefreshCw, Mail, Trash2, CheckCheck, Eye, Inbox } from "lucide-react";

interface RegistrationEnquiry {
  id: number;
  full_name: string;
  email: string;
  phone_number: string;
  city: string;
  work_entity: string;
  event_id: number | null;
  event_title: string | null;
  is_read: boolean;
  created_at: string;
}

type LoadState = "loading" | "ready" | "error";

export function RegistrationEnquiries() {
  const [state, setState] = useState<LoadState>("loading");
  const [enquiries, setEnquiries] = useState<RegistrationEnquiry[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadEnquiries = useCallback(async () => {
    setState("loading");
    try {
      const response = await api.get<RegistrationEnquiry[]>("/api/admin/registrations");
      setEnquiries(response.success && response.data ? response.data : []);
      setState("ready");
    } catch (error) {
      console.error("Failed to load registration enquiries:", error);
      setState("error");
    }
  }, []);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  const toggleRead = async (enquiry: RegistrationEnquiry) => {
    const nextRead = !enquiry.is_read;
    // Optimistic update
    setEnquiries((prev) =>
      prev.map((e) => (e.id === enquiry.id ? { ...e, is_read: nextRead } : e)),
    );
    try {
      await api.put(`/api/admin/registrations/${enquiry.id}`, { is_read: nextRead });
      toast.success(nextRead ? "Marked as read" : "Marked as unread");
    } catch (error) {
      // Revert on failure
      setEnquiries((prev) =>
        prev.map((e) => (e.id === enquiry.id ? { ...e, is_read: enquiry.is_read } : e)),
      );
      console.error("Failed to update enquiry:", error);
      toast.error("Failed to update enquiry");
    }
  };

  const confirmDelete = async () => {
    if (deleteId === null) return;
    setIsDeleting(true);
    try {
      await api.delete(`/api/admin/registrations/${deleteId}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== deleteId));
      toast.success("Enquiry deleted");
      setDeleteId(null);
    } catch (error) {
      console.error("Failed to delete enquiry:", error);
      toast.error("Failed to delete enquiry");
    } finally {
      setIsDeleting(false);
    }
  };

  const unreadCount = enquiries.filter((e) => !e.is_read).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl text-primary">Registration Enquiries</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Delegate registrations submitted from the Register popup on the public site.
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 font-medium text-accent">
                <Inbox className="h-3.5 w-3.5" />
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={loadEnquiries} disabled={state === "loading"}>
          <RefreshCw className={`h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {state === "error" && (
        <Card className="border-destructive/30 bg-destructive/5 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-foreground">Could not load registration enquiries</p>
              <p className="text-sm text-muted-foreground">
                Check that the API is running, then try again.
              </p>
            </div>
            <Button variant="outline" onClick={loadEnquiries}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </Card>
      )}

      {state === "loading" ? (
        <Card className="p-5">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : state === "ready" && enquiries.length === 0 ? (
        <Card className="p-10 text-center">
          <Inbox className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium text-foreground">No registration enquiries yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Registrations submitted from the public Register popup will appear here.
          </p>
        </Card>
      ) : state === "ready" ? (
        <Card className="overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[180px]">Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead className="hidden md:table-cell">City</TableHead>
                <TableHead className="hidden lg:table-cell">Work Entity</TableHead>
                <TableHead className="hidden xl:table-cell">Event</TableHead>
                <TableHead className="w-[90px]">Status</TableHead>
                <TableHead className="hidden sm:table-cell w-[120px]">Date</TableHead>
                <TableHead className="text-right w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enquiries.map((enquiry) => (
                <TableRow
                  key={enquiry.id}
                  className={enquiry.is_read ? undefined : "bg-accent/5"}
                >
                  <TableCell className="font-medium text-foreground">{enquiry.full_name}</TableCell>
                  <TableCell>
                    <a
                      href={`mailto:${enquiry.email}`}
                      className="text-foreground hover:text-accent break-all"
                    >
                      {enquiry.email}
                    </a>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {enquiry.phone_number}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {enquiry.city}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {enquiry.work_entity}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell text-muted-foreground">
                    {enquiry.event_title ? (
                      <span className="line-clamp-1 max-w-[180px]" title={enquiry.event_title}>
                        {enquiry.event_title}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {enquiry.is_read ? (
                      <Badge variant="secondary">Read</Badge>
                    ) : (
                      <Badge className="bg-accent text-accent-foreground">New</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-xs text-muted-foreground whitespace-nowrap">
                    {format(new Date(enquiry.created_at), "d MMM yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title={enquiry.is_read ? "Mark as unread" : "Mark as read"}
                        onClick={() => toggleRead(enquiry)}
                      >
                        {enquiry.is_read ? (
                          <Mail className="h-4 w-4" />
                        ) : (
                          <CheckCheck className="h-4 w-4 text-accent" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        title="Delete enquiry"
                        onClick={() => setDeleteId(enquiry.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this registration enquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The enquiry record will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
